import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { CheckCircle, XCircle, AlertTriangle, Camera, Keyboard } from 'lucide-react';
import { verifyToken } from '../../services/guardService';
import { useOccupancy } from '../../context/OccupancyContext';
import '../../styles/ScanPage.css';

const STATUS_CONFIG = {
  valid:   { bg: '#22c55e', label: 'ACCÈS AUTORISÉ',  Icon: CheckCircle },
  expired: { bg: '#ef4444', label: 'TOKEN EXPIRÉ',    Icon: XCircle },
  used:    { bg: '#f59e0b', label: 'DÉJÀ UTILISÉ',   Icon: AlertTriangle },
  invalid: { bg: '#ef4444', label: 'TOKEN INVALIDE',  Icon: XCircle },
  revoked: { bg: '#ef4444', label: 'TOKEN RÉVOQUÉ',   Icon: XCircle },
};

export default function ScanPage() {
  const navigate = useNavigate();
  const { count, refresh } = useOccupancy();

  const [result, setResult]         = useState(null);
  const [loading, setLoading]       = useState(false);
  const [mode, setMode]             = useState('camera'); // 'camera' | 'manual'
  const [manualInput, setManualInput] = useState('');

  const scannerRef  = useRef(null);
  const timerRef    = useRef(null);
  const inputRef    = useRef(null);
  const processingRef = useRef(false); // prevent double-scan

  const stopScanner = useCallback(async () => {
    const instance = scannerRef.current;
    scannerRef.current = null;

    if (!instance) return;

    try {
      await Promise.resolve(instance.stop());
    } catch {}

    try {
      await Promise.resolve(instance.clear());
    } catch {}
  }, []);

  // ── camera scanner lifecycle ───────────────────────────────────────────────
  useEffect(() => {
    if (mode !== 'camera' || result) return;

    let html5Qrcode;
    let stopped = false;
    let isRunning = false; // only true after start() resolves

    const startScanner = async () => {
      const el = document.getElementById('qr-reader');
      if (!el || stopped) return;

      try {
        html5Qrcode = new Html5Qrcode('qr-reader');
        scannerRef.current = html5Qrcode;

        const cameras = await Html5Qrcode.getCameras();
        if (!cameras || cameras.length === 0 || stopped) return;

        // Prefer back-facing camera on mobile
        const camera = cameras.find(c => /back|rear|environment/i.test(c.label)) || cameras[cameras.length - 1];

        await html5Qrcode.start(
          camera.id,
          { fps: 10, qrbox: { width: 250, height: 250 } },
          (decodedText) => {
            if (!processingRef.current) {
              processingRef.current = true;
              handleScan(decodedText);
            }
          },
          () => {} // ignore per-frame decode errors
        );

        // If cleanup already ran while start() was in flight, stop immediately
        if (stopped) {
          await Promise.resolve(html5Qrcode.stop()).catch(() => {});
          await Promise.resolve(html5Qrcode.clear()).catch(() => {});
          return;
        }

        isRunning = true;
      } catch {
        // Camera permission denied or not available — silently fail
      }
    };

    startScanner();

    return () => {
      stopped = true;
      if (isRunning) {
        stopScanner();
        return;
      }

      Promise.resolve(scannerRef.current?.clear()).catch(() => {});
      scannerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, result, stopScanner]);

  // ── auto-focus manual input ────────────────────────────────────────────────
  useEffect(() => {
    if (mode === 'manual' && !result && inputRef.current) {
      inputRef.current.focus();
    }
  }, [mode, result]);

  // ── auto-dismiss result after 4 s ─────────────────────────────────────────
  useEffect(() => {
    if (result) {
      timerRef.current = setTimeout(dismissResult, 4000);
      return () => clearTimeout(timerRef.current);
    }
  }, [result]);

  const dismissResult = () => {
    setResult(null);
    setManualInput('');
    processingRef.current = false;
  };

  const handleScan = useCallback(async (token) => {
    if (!token || loading) return;
    setLoading(true);
    try {
      const res = await verifyToken(token.trim());
      await stopScanner();
      setResult(res.data);
      if (res.data.status === 'valid') refresh();
    } catch {
      await stopScanner();
      setResult({ status: 'invalid', visitorName: '', visitType: '' });
    } finally {
      setLoading(false);
    }
  }, [loading, refresh, stopScanner]);

  // ── scan screen ────────────────────────────────────────────────────────────
  return (
    <div className="scan-page">
        <div className="scan-header">
          <h1>Poste de sécurité</h1>
          <div className="occupancy-badge">
            Occupants actuels : <strong>{count}</strong>
          </div>
        </div>

        {/* Mode toggle */}
        <div className="scan-mode-toggle">
          <button
            className={`mode-btn ${mode === 'camera' ? 'active' : ''}`}
            onClick={() => setMode('camera')}
          >
            <Camera size={16} /> Caméra
          </button>
          <button
            className={`mode-btn ${mode === 'manual' ? 'active' : ''}`}
            onClick={() => setMode('manual')}
          >
            <Keyboard size={16} /> Saisie manuelle
          </button>
        </div>

        {mode === 'camera' ? (
          <div className="scan-camera-area">
            <p className="scan-instructions">Pointez la caméra vers le code QR du visiteur</p>
            <div id="qr-reader" className="qr-reader-container" />
          </div>
        ) : (
          <div className="scan-area">
            <p className="scan-instructions">Entrez ou collez le token QR manuellement</p>
            <input
              ref={inputRef}
              type="text"
              className="scan-input"
              value={manualInput}
              onChange={e => setManualInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleScan(manualInput)}
              placeholder="Token QR..."
            />
            <button
              className="btn btn-scan"
              onClick={() => handleScan(manualInput)}
              disabled={!manualInput || loading}
              style={{ minHeight: '80px' }}
            >
              {loading ? 'Vérification...' : 'Vérifier'}
            </button>
          </div>
        )}

        <div className="scan-actions">
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/guard/manual')}
            style={{ minHeight: '80px' }}
          >
            Entrée manuelle visiteur
          </button>
        </div>

        {result && (() => {
          const cfg = STATUS_CONFIG[result.status] || STATUS_CONFIG.invalid;
          const { Icon } = cfg;

          return (
            <div className="scan-result-fullscreen" style={{ backgroundColor: cfg.bg }}>
              <div className="scan-result-content">
                <Icon size={96} strokeWidth={1.5} color="#fff" className="scan-result-icon-svg" />
                <div className="scan-result-label">{cfg.label}</div>
                {result.isVip && (
                  <div className="scan-result-vip-badge">★ VIP</div>
                )}
                {result.visitorName && (
                  <div className="scan-result-name">{result.visitorName}</div>
                )}
                {result.visitType && (
                  <div className="scan-result-type">{result.visitType.replace(/_/g, ' ')}</div>
                )}
              </div>
              <button
                className="scan-result-dismiss"
                onClick={dismissResult}
                style={{ minHeight: '80px' }}
              >
                Retour au scan
              </button>
            </div>
          );
        })()}
      </div>
  );
}
